import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { isNetworkError, reportError } from "@/lib/errorReporting";
import { useTenant } from "@/hooks/useTenant";
import { countryName, type Tenant } from "@/lib/tenants";
import {
  OWNER_PRICING_KEY,
  resolveSiteCopy,
  splitCopyList,
  type CopySource,
  type CopyVars,
  type ResolvedCopy,
  type SiteCopyPage,
} from "@/lib/siteCopy";

// Mirrors useSiteAsset: the whole (small) site_copy table is fetched once per
// session and every consumer resolves from that cache, so a page never pays a
// request per string. Prerendered pages ship the rows in __REACT_QUERY_STATE__.

export interface SiteCopyRow {
  id: string;
  key: string;
  tenant_id: string | null;
  value: string;
  updated_at: string;
}

export const SITE_COPY_QUERY_KEY = ["site_copy"] as const;

export function useSiteCopy() {
  return useQuery({
    queryKey: SITE_COPY_QUERY_KEY,
    queryFn: async (): Promise<SiteCopyRow[]> => {
      const { data, error } = await supabase
        .from("site_copy" as never)
        .select("id, key, tenant_id, value, updated_at")
        .order("updated_at", { ascending: false });
      if (error) {
        // A missing table (migration not pasted yet) or an RLS hiccup must
        // never take a page down: defaults render instead.
        console.warn("[useSiteCopy] failed to fetch", error);
        // Reported so ops-health sees it (a missing table once hid every copy edit);
        // a dropped connection is not a bug and is not reported.
        if (!isNetworkError(error.message ?? "")) reportError("error", `site_copy read failed: ${error.code ?? ""} ${error.message}`, { source: "useSiteCopy" });
        return [];
      }
      return (data ?? []) as unknown as SiteCopyRow[];
    },
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** The placeholders every copy template can use. */
export const copyVarsForTenant = (tenant: Tenant): CopyVars => {
  const areas = tenant.areas ?? [];
  return {
    siteName: tenant.siteName,
    city: tenant.cityLabel,
    region: tenant.regionLabel,
    country: countryName(tenant.country),
    areasShort: `${areas.slice(0, 4).join(", ")}${areas.length > 4 ? " and more" : ""}`,
    areaCount: String(areas.length),
    areaList: areas.join(", "),
  };
};

/** One resolver for many keys (lists, Seo, admin previews). */
export function useCopyResolver(): (key: string, extraVars?: Record<string, string>) => ResolvedCopy {
  const tenant = useTenant();
  const { data: rows } = useSiteCopy();
  return useCallback(
    (key: string, extraVars: Record<string, string> = {}) =>
      resolveSiteCopy(rows, tenant.id, key, { ...copyVarsForTenant(tenant), ...extraVars }),
    [rows, tenant],
  );
}

/** Resolved text for one key. */
export function useCopy(key: string, extraVars?: Record<string, string>): string {
  return useCopyResolver()(key, extraVars).value;
}

/** Resolved bullets for a list key. */
export function useCopyList(key: string): string[] {
  return splitCopyList(useCopy(key));
}

export interface PageCopy {
  t: (key: string) => string;
  list: (key: string) => string[];
  source: (key: string) => CopySource;
  vars: CopyVars;
}

/** Every key of a page with the tenant's placeholders bound once. The owner
 *  landing also exposes {pricing} from its own pricing claim. */
export function useCopyPage(page: SiteCopyPage): PageCopy {
  const tenant = useTenant();
  const { data: rows } = useSiteCopy();
  return useMemo(() => {
    const base = copyVarsForTenant(tenant);
    const vars: CopyVars =
      page === "owners"
        ? { ...base, pricing: resolveSiteCopy(rows, tenant.id, OWNER_PRICING_KEY, base).value }
        : base;
    const resolve = (key: string) => resolveSiteCopy(rows, tenant.id, key, vars);
    return {
      t: (key: string) => resolve(key).value,
      list: (key: string) => splitCopyList(resolve(key).value),
      source: (key: string) => resolve(key).source,
      vars,
    };
  }, [rows, tenant, page]);
}
